import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Toaster } from "sonner";
import SEO from "../components/SEO";
import {
  ArrowRight,
  Clapperboard,
  ScanLine,
  Atom,
  Layers3,
  Sparkles,
  Plane,
  Hexagon,
  Orbit,
} from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { StudiosInquiryForm } from "../components/site/StudiosInquiryForm";

const STUDIO_SERVICES = [
  {
    icon: Clapperboard,
    title: "Aerial Cinematics",
    desc: "Cinema-grade close action drone cinematography for features, commercials, and episodic television.",
    tag: "Film",
  },
  {
    icon: ScanLine,
    title: "VFX Scanning",
    desc: "Photogrammetric and LiDAR scanning of props, vehicles, and environments — delivered as production-ready 3D assets for visual effects.",
    tag: "VFX",
  },
  {
    icon: Atom,
    title: "Gaussian Splatting",
    desc: "Photo-real radiance-field reconstructions captured from drone and ground passes for next-generation set extension and CGI integration.",
    tag: "Splats",
  },
  {
    icon: Layers3,
    title: "Set Scanning",
    desc: "Full-set captures with millimetre-grade detail for previs, virtual production, set extension, and post-production reference.",
    tag: "Sets",
  },
  {
    icon: Orbit,
    title: "Photogrammetry & LiDAR Data",
    desc: "Both acquisition methods used in tandem — set, prop, and environment capture delivered as clean meshes and textures ready for production pipelines.",
    tag: "Methods",
  },
  {
    icon: Hexagon,
    title: "3D Data Meshing",
    desc: "Raw photogrammetry and LiDAR processed into clean, optimised meshes with PBR-ready textures — shot-ready for VFX, previs, and production pipelines.",
    tag: "Meshes",
  },
];

const STUDIO_STATS = [
  { value: "8K", label: "Aerial Cinematics" },
  { value: "PRECISION", label: "Close-Proximity Flight Capture" },
  { value: "3D", label: "VFX & Set Design Data" },
  { value: "GLOBAL", label: "Deployment Range" },
];

const STUDIO_CREDS = [
  "Cinema-grade Inspire 3 / Close action workflows",
  "RAW, ProRes, and CinemaDNG pipelines",
  "Precision data + cinematic aerials in one mobilization",
  "Transport Canada Advanced RPAS operations",
];

const STUDIO_SHOTS = [
  {
    src: "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/huf6amc1_Anthony%20Urso%20-%20DJI%20Inspire%203%20Promo%202.00_00_06_22.Still001.jpg",
    title: "Aerial Cinematics",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/fbvz8ctg_image1.JPG",
    title: "On-set Support",
    subtitle: "VFX Scanning",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/4bkgmnmk_A003C0032_260209_U82V03.00_00_09_55.Still003.jpg",
    title: "Close Action",
    subtitle: "Dual Operator",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/5b8xum7w_meshweb2.jpg",
    title: "Set Scanning",
    subtitle: "Lionsgate — The Long Walk",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/l26t9hn0_image4.png",
    title: "Interior Meshing",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/w5q585ya_meshweb1.jpg",
    title: "Street Scan",
  },
];

export default function Studios() {
  return (
    <div className="bg-black min-h-screen text-white" data-testid="studios-page">
      <SEO
        title="Aerial & Drone Cinematics Winnipeg | Thunderbird Geospatial Inc."
        description="Thunderbird Geospatial Inc. serves the Winnipeg area offering drone videography & photography, VFX, motion effects, video production, and aerial cinema services."
        canonical="https://tbirdgeo.com/film-vfx/"
      />
      <Toaster theme="dark" richColors position="top-center" />
      <Navbar />

      {/* Hero */}
      <section
        data-testid="studios-hero"
        className="relative min-h-[92vh] overflow-hidden bg-black tb-noise"
      >
        <motion.div
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.2, 0.8, 0.2, 1] }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/evd9fdra_thumbnail_IMG_1278.jpg')",
            filter: "saturate(1.05) contrast(1.05)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.55)_70%,#000_100%)]" />
        <div className="absolute inset-0 tb-grid-overlay mix-blend-overlay opacity-70" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-20 min-h-[92vh] flex flex-col justify-center">
          <motion.img
            src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/eel03kf3_FullLogo_Transparent_NoBuffer%20%2815%29.png"
            alt="TGI Studios"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="h-16 md:h-20 w-auto object-contain self-start mb-5"
            draggable="false"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center self-start gap-2 rounded-full border border-[#3DC0AF]/40 bg-[#3DC0AF]/10 px-3 py-1 text-[11px] tracking-[0.2em] uppercase text-[#3DC0AF] font-mono"
          >
            <Sparkles className="h-3 w-3" />
            TGI Studios — VFX Support & Aerial Cinematics
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-6 max-w-5xl text-5xl sm:text-6xl lg:text-7xl font-display font-black tracking-tighter leading-[0.95]"
          >
            Cinematic aerials{" "}
            <span className="italic font-semibold text-zinc-400">&amp;</span>{" "}
            <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-white via-white to-[#3DC0AF] bg-clip-text text-transparent">
              VFX-ready captures.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-6 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
          >
            The film and VFX arm of Thunderbird Geospatial. We fly, capture, and
            deliver close action cinema-grade aerials, hi-resolution imaging, and
            3D scan data for productions in Manitoba and across the Globe.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#inquiry"
              data-testid="studios-cta-contact"
              className="group inline-flex items-center gap-3 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] text-white px-7 py-4 text-sm font-medium transition-colors"
            >
              Start a production
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/"
              data-testid="studios-back-home"
              className="group inline-flex items-center gap-3 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 text-white px-7 py-4 text-sm font-medium transition-colors"
            >
              <Plane className="h-4 w-4" />
              Geomatics & surveying
            </Link>
          </motion.div>

          {/* Studio stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.8 }}
            className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-white/10 border-t border-white/10 pt-8"
          >
            {STUDIO_STATS.map((s, i) => (
              <div key={s.label} className="md:px-6" data-testid={`studios-stat-${i}`}>
                <div className="font-display font-bold text-xl md:text-2xl tracking-tight">
                  {s.value}
                </div>
                <div className="mt-2 text-sm text-zinc-400 leading-snug">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-black" />
      </section>

      {/* Services */}
      <section
        data-testid="studios-services"
        className="relative py-24 md:py-32 bg-black"
      >
        <div className="absolute inset-0 tb-grid-dense opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
                / CAPABILITIES
              </div>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter leading-[0.95] max-w-3xl">
                Production-ready from plate to post.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md">
              One mobilization. Cinema deliverables plus survey-grade data your VFX
              team can actually use.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {STUDIO_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  data-testid={`studios-service-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: (i % 6) * 0.06 }}
                  className="tb-card group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-7"
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-black/40 text-[#5EE3CE] group-hover:text-white group-hover:bg-[#3DC0AF] group-hover:border-[#3DC0AF] transition-all">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">
                      {s.tag}
                    </span>
                  </div>
                  <h3 className="mt-8 text-xl md:text-2xl font-display font-bold tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                    {s.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reel / Frames */}
      <section
        data-testid="studios-reel"
        className="relative py-24 md:py-32 bg-[#05070F] overflow-hidden"
      >
        <div className="absolute inset-0 tb-grid-dense opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
                / FRAMES
              </div>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter leading-[0.95] max-w-2xl">
                Capturing close action environments for both camera and post.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-sm">
              Detailed 3D environments and props from film sets and cinematic
              drone operations captured on location.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {STUDIO_SHOTS.map((shot, i) => (
              <motion.div
                key={shot.src}
                data-testid={`studios-frame-${i}`}
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/10 bg-black"
              >
                <img
                  src={shot.src}
                  alt={shot.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display text-lg md:text-xl font-bold leading-tight">
                      {shot.title}
                    </div>
                    {shot.subtitle && (
                      <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-zinc-300 font-mono truncate">
                        {shot.subtitle}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#5EE3CE] shrink-0">
                    FRAME · {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Creds + CTA */}
      <section
        id="inquiry"
        data-testid="studios-cta"
        className="relative py-24 md:py-32 bg-black"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
              / BUILT FOR PRODUCTION
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-display font-black tracking-tighter leading-[0.95]">
              Pilots, post, and pipeline — under one roof.
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              TGI Studios exists because scanning precision and cinematic quality
              usually require two crews. We deliver both on a single call-sheet.
            </p>
            <ul className="mt-8 space-y-3">
              {STUDIO_CREDS.map((c, i) => (
                <li
                  key={c}
                  data-testid={`studios-cred-${i}`}
                  className="flex items-start gap-3 text-zinc-300 text-sm"
                >
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#3DC0AF] shadow-[0_0_0_3px_rgba(61,192,175,0.18)]" />
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <StudiosInquiryForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
